import type {DOMAttributes, FocusableElement} from '@react-types/shared';
import type {GridCollection, GridNode} from '@react-types/grid';
import type {RefObject} from 'react';
import type {GridState} from '../../@react-stately/grid';

import type {SelectableItemStates} from '../selection';
import {useSelectableItem} from '../selection';
import {gridMap} from './utils';

export interface GridRowProps<T> {
  /** An object representing the grid row. Contains all the relevant information that makes up the grid row. */
  node: GridNode<T>;
  /** Whether the grid row is contained in a virtual scroller. */
  isVirtualized?: boolean;
  /** Whether selection should occur on press up instead of press down. */
  shouldSelectOnPressUp?: boolean;
  /**
   * Handler that is called when a user performs an action on the row.
   * Please use onCellAction at the collection level instead.
   * @deprecated
   * */
  onAction?: () => void;
}

export interface GridRowAria extends SelectableItemStates {
  /** Props for the grid row element. */
  rowProps: DOMAttributes;
  /** Whether the row is currently in a pressed state. */
  isPressed: boolean;
}

/**
 * Provides the behavior and accessibility implementation for a row in a grid.
 * @param props - Props for the row.
 * @param state - State of the parent grid, as returned by `useGridState`.
 */
export function useGridRow<
  T,
  C extends GridCollection<T>,
  S extends GridState<T, C>,
>(
  props: GridRowProps<T>,
  state: S,
  ref: RefObject<FocusableElement>,
): GridRowAria {
  const {node, isVirtualized, shouldSelectOnPressUp, onAction} = props;

  const {
    actions: {onRowAction},
  } = gridMap.get(state) ?? {actions: {}};
  const {itemProps, ...states} = useSelectableItem({
    selectionManager: state.selectionManager,
    key: node.key,
    ref,
    isVirtualized,
    shouldSelectOnPressUp,
    onAction: onRowAction ? () => onRowAction(node.key) : onAction,
    isDisabled: state.collection.size === 0,
  });

  const isSelected = state.selectionManager.isSelected(node.key);

  const rowProps: DOMAttributes = {
    role: 'row',
    'aria-selected':
      state.selectionManager.selectionMode !== 'none' ? isSelected : undefined,
    'aria-disabled': states.isDisabled || undefined,
    ...itemProps,
  };

  if (isVirtualized) {
    rowProps['aria-rowindex'] = (node.index ?? 0) + 1; // aria-rowindex is 1 based
  }

  return {
    rowProps,
    ...states,
  };
}
